import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../context/ConfigContext';
import { handlePhoneChange as formatPhone } from '../utils/phoneFormat';
import { smsIntakeDecision } from '../utils/smsIntakeChoice';
import { intakeAddonsPayload } from '../utils/intakeAddons';
import SmsConsentChoice from '../components/SmsConsentChoice';
import usePageMeta, { SITE_URL } from '../hooks/usePageMeta';

// Driveway size copy.
//
// SIZE_FRAMING is shown once, above the options on the primary address. It
// deliberately says nothing about any particular tier, so a company renaming or
// repricing its tiers cannot make this sentence false. It is NOT repeated on
// each additional property: on a three property form that would be the same
// long sentence three times, and it has already been read by then.
//
// SIZE_HELPER is addressed to the person filling the form in, so it lives here
// and nowhere else in the app. The owner-facing screens show the framing
// sentence alone.
//
// SIZE_CONFIRMATION_DEFAULT is only a fallback. When a company has written its
// own line into companies.intake_size_confirmation_text, that text is used
// instead. A promise about how the company behaves belongs to the company.
const SIZE_FRAMING = 'Count parking spaces on the driveway surface, counting both width and length, not how many cars the customer owns.';
const SIZE_HELPER = 'How many cars could park on your driveway at once, side by side and end to end, whether or not you park that many?';
const SIZE_CONFIRMATION_DEFAULT = 'We confirm the size at your first visit. If it is larger than what you selected, we will tell you before we service it.';

// "Not sure" is a real answer and gets a real value on the wire, matching
// NOT_SURE_SIZE in server/server.js.
//
// An empty string would not do. That is also what an old cached client sends and
// what a bot sends, and "I do not know my driveway size" is a different thing
// from "this field never got filled in". The server flags both for the owner to
// look at, but only one of them is the customer telling us something.
const NOT_SURE_SIZE = 'not_sure';

// The salt question, per address: the same three options as the season
// confirm page, each a customers.salt_preference value.
export const SALT_CHOICES = [
  { value: 'never', label: 'No salt' },
  { value: 'always', label: 'Always (salt every visit)' },
  { value: 'as_needed', label: 'When icy (salt when it is icy)' },
];

function IntakeForm() {
  const { companyCode } = useParams();
  const { getAppName } = useConfig();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [leadSources, setLeadSources] = useState([]);
  const [disclaimerEnabled, setDisclaimerEnabled] = useState(true);
  const [disclaimerText, setDisclaimerText] = useState('');
  // The server's sha256 of the terms as rendered, echoed back on submit so the
  // server can tell whether the text changed after this page loaded (409).
  const [disclaimerSha256, setDisclaimerSha256] = useState(null);
  // Empty is a real, expected state: a company can deactivate every tier. When
  // it is empty the size question is omitted from the page entirely rather than
  // rendered as an empty dropdown, and the form still submits.
  const [sizeOptions, setSizeOptions] = useState([]);
  const [sizeConfirmationText, setSizeConfirmationText] = useState('');
  // Text-message consent, a required Yes / No radio pair. Shown only when the
  // server says the company offers texting, with both wordings exactly as the
  // server rendered them; the server stores its own rendering of the Yes text
  // on submit. smsChoiceError is the inline message under the radios.
  const [smsAvailable, setSmsAvailable] = useState(false);
  const [smsConsentText, setSmsConsentText] = useState('');
  const [smsDeclineText, setSmsDeclineText] = useState('');
  const [smsChoiceError, setSmsChoiceError] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: 'WI',
    zip: '',
    phone: '',
    email: '',
    leadSource: '',
    leadSourceOther: '',
    referredByName: '',
    driveway: true,
    sidewalks: false,
    walkways: false,
    // customers.salt_preference: never, always (every visit) or as_needed
    // (when icy), the three the season confirm page offers.
    saltPreference: 'never',
    notes: '',
    // The driveway size the visitor selects for THIS address, as a
    // pricing_tiers id in string form. Empty means no answer, which is allowed:
    // the question is never required and an unanswered one stores NULL.
    requestedSnowTierId: '',
    snowfallThreshold: '',
    // Seasonal by default (owner Q8); on-call is the customer's choice.
    servicePreference: 'seasonal',
    // Honeypot field
    website: '',
    // Terms acceptance
    termsAccepted: false,
    // Email opt-in
    emailSubscribed: true,
    // Text-message consent radio: '' until the visitor picks 'yes' or 'no',
    // never preselected. Sent as the boolean smsConsent, not under this name.
    smsChoice: '',
  });

  // The on-call premium percentage when the company prices snow by depth
  // (design 4.3, owner Q8), else null.
  const [onCallPremiumPercent, setOnCallPremiumPercent] = useState(null);

  // The public add-ons offered as checkboxes ({ id, name, detailLabel }), and
  // what was ticked: { [id]: { checked, detail } }. Not part of formData, so
  // it is sent as the addons list the server reads, not field by field.
  const [addonOptions, setAddonOptions] = useState([]);
  const [addonChoices, setAddonChoices] = useState({});

  // Additional properties state
  const [additionalProperties, setAdditionalProperties] = useState([]);
  const [expandedProperties, setExpandedProperties] = useState({});

  // A per-company sign-up page must not compete with the landing page in
  // search: noindex here for crawlers that run the bundle, and an
  // X-Robots-Tag header from netlify.toml for those that do not. The copy and
  // image match netlify/edge-functions/intake-preview.js, which sets the same
  // tags for link scrapers before the bundle ever runs.
  const { pathname } = useLocation();
  usePageMeta({
    title: companyName ? `${companyName} sign-up` : 'Snow removal sign-up',
    description: 'Driveways, sidewalks and steps cleared all winter. Fill in the form and we will be in touch.',
    path: pathname,
    image: `${SITE_URL}/og-intake.png`,
    robots: 'noindex',
  });

  useEffect(() => {
    const loadFormData = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      const fullUrl = `${apiUrl}/api/public/intake/${companyCode}`;

      try {
        const response = await axios.get(fullUrl);
        setCompanyName(response.data.companyName);
        setLeadSources(response.data.leadSources);
        // Load disclaimer settings from company
        setDisclaimerEnabled(response.data.disclaimerEnabled ?? true);
        setDisclaimerText(response.data.disclaimerText || '');
        setDisclaimerSha256(response.data.disclaimerSha256 || null);
        // Absent from an older server: no radios. Both wordings are needed to
        // show the pair, so a server that sends Yes without No shows neither.
        setSmsAvailable(response.data.smsAvailable === true && Boolean(response.data.smsConsentText)
          && Boolean(response.data.smsDeclineText));
        setSmsConsentText(response.data.smsConsentText || '');
        setSmsDeclineText(response.data.smsDeclineText || '');
        // Both keys are additive. An older server that does not send them
        // leaves sizeOptions empty, which omits the question rather than
        // breaking the form.
        setSizeOptions(Array.isArray(response.data.sizeOptions) ? response.data.sizeOptions : []);
        setSizeConfirmationText(response.data.sizeConfirmationText || SIZE_CONFIRMATION_DEFAULT);
        // Absent from an older server: no add-on section.
        setAddonOptions(Array.isArray(response.data.addonOptions) ? response.data.addonOptions : []);
        // Absent from an older server: no line under On-call.
        setOnCallPremiumPercent(parseFloat(response.data.onCallPremiumPercent) > 0
          ? parseFloat(response.data.onCallPremiumPercent) : null);
        setError('');
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This signup form is not available. Please check the link and try again.');
        } else {
          // Include detailed error info for debugging on mobile
          const errorDetails = [];
          if (err.message) errorDetails.push(err.message);
          if (err.response?.status) errorDetails.push(`Status: ${err.response.status}`);
          if (err.response?.data?.error) errorDetails.push(err.response.data.error);
          if (err.code) errorDetails.push(`Code: ${err.code}`);

          const detailStr = errorDetails.length > 0 ? ` (${errorDetails.join(', ')})` : '';
          setError(`Unable to load form${detailStr}. Please try again later.`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (companyCode) {
      loadFormData();
    } else {
      setError('Invalid signup link. Please contact us directly.');
      setLoading(false);
    }
  }, [companyCode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'phone') {
      const { formatted } = formatPhone(value);
      setFormData(prev => ({ ...prev, phone: formatted }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const addProperty = () => {
    const newProperty = {
      id: Date.now(), // Temporary ID for React key
      address: '',
      city: formData.city || '',
      state: formData.state || 'WI',
      zip: '',
      driveway: true,
      sidewalks: false,
      walkways: false,
      saltPreference: 'never',
      notes: '',
      // NOT pre-filled from the primary address, unlike city and state above.
      // Those usually do match; size usually does not. A detached garage pad
      // and a rental duplex are the common second property, and inheriting the
      // first answer would record one the customer never gave.
      requestedSnowTierId: '',
    };
    setAdditionalProperties(prev => [...prev, newProperty]);
    setExpandedProperties(prev => ({ ...prev, [newProperty.id]: true }));
  };

  const removeProperty = (propertyId) => {
    setAdditionalProperties(prev => prev.filter(p => p.id !== propertyId));
    setExpandedProperties(prev => {
      const newExpanded = { ...prev };
      delete newExpanded[propertyId];
      return newExpanded;
    });
  };

  const handlePropertyChange = (propertyId, e) => {
    const { name, value, type, checked } = e.target;
    setAdditionalProperties(prev => prev.map(p => {
      if (p.id === propertyId) {
        return { ...p, [name]: type === 'checkbox' ? checked : value };
      }
      return p;
    }));
  };

  // The definition for the size currently selected on one address, or null.
  //
  // Read from pricing_tiers.description, which the owner writes. A tier with no
  // description renders no definition line at all: there is deliberately no
  // hardcoded fallback keyed on the tier name, because a company that renamed
  // Medium to something of its own would then be shown a definition it never
  // wrote.
  const sizeDefinition = (tierId) => {
    if (!tierId) return null;
    const option = sizeOptions.find(o => String(o.id) === String(tierId));
    return (option && option.description) || null;
  };

  // The label for one size in the dropdown: the tier name, then the owner's
  // definition in parentheses.
  //
  // The definition has to be on the option itself. Rendered under the select it
  // only describes the size already chosen, so a visitor comparing sizes for the
  // first time had to select each one in turn to read what it meant. The line
  // under the select stays as well - it is the confirmation of the choice, and a
  // long label is truncated by the native dropdown on a narrow screen.
  //
  // A tier with no description renders the bare name and no empty parentheses,
  // for the same reason sizeDefinition has no hardcoded fallback: the text
  // belongs to the owner, and inventing one puts words in their mouth.
  const sizeOptionLabel = (option) => (
    option.description ? `${option.name} (${option.description})` : option.name
  );

  const togglePropertyExpanded = (propertyId) => {
    setExpandedProperties(prev => ({
      ...prev,
      [propertyId]: !prev[propertyId]
    }));
  };

  // The fields an additional property must have before the form can be sent,
  // listed in the order they are rendered so a blocked visitor is taken to the
  // first gap rather than the last. The size select is only in the list when
  // there is a size question at all, matching the required= on that control: a
  // company with no active tiers does not render it and must not be held to it.
  //
  // This duplicates what required= already says on each control. It has to: the
  // whole point is the case where those controls are not in the DOM to say it.
  const firstMissingPropertyField = (property) => {
    const names = ['address', 'city', 'state', 'zip'];
    if (sizeOptions.length > 0) names.push('requestedSnowTierId');
    return names.find(name => !String(property[name] ?? '').trim()) || null;
  };

  // Set for one render when a blocked submit has expanded a collapsed property,
  // naming the field to take the visitor to. It cannot be acted on inside
  // handleSubmit: expanding is a state change, and the property's inputs are
  // only mounted on the render that follows it, so the element does not exist
  // yet at the point the decision is made.
  const pendingPropertyFieldRef = useRef(null);

  useEffect(() => {
    const pending = pendingPropertyFieldRef.current;
    if (!pending) return;
    // Cleared before doing anything, so a later re-render for an unrelated
    // reason cannot scroll the page a second time.
    pendingPropertyFieldRef.current = null;

    const section = document.getElementById(`property-${pending.propertyId}`);
    const field = section && section.querySelector(`[name="${pending.fieldName}"]`);
    if (!field) return;

    // Hand the field back to the browser rather than describing the problem
    // ourselves. reportValidity re-runs the browser's own check on that one
    // control and fires the same invalid event a blocked submit would have, so
    // handleInvalid does the focusing and centring and the visitor gets
    // the native message bubble. A property that was collapsed while empty then
    // ends up looking exactly like any other empty required field.
    if (typeof field.reportValidity === 'function') {
      field.reportValidity();
      return;
    }
    // Older browsers without constraint validation get the movement at least.
    if (typeof field.focus === 'function') {
      field.focus({ preventScroll: true });
    }
    if (typeof field.scrollIntoView === 'function') {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [expandedProperties]);

  // Set for the duration of one blocked submit, so only the FIRST invalid field
  // is scrolled to. It is a ref rather than state because nothing renders from
  // it and a re-render between the invalid events would lose the flag.
  const invalidHandledRef = useRef(false);

  // A blocked submit used to be silent. The browser refuses to submit when a
  // required field is empty and never calls handleSubmit, so no error appears,
  // and if the offending field is scrolled off screen the visitor sees a dead
  // Submit button and leaves.
  //
  // This is the browser's own constraint validation, not a second validator: the
  // fields already carry required, and the browser fires an invalid event at
  // each failing field, in document order, when it blocks the submit. That event
  // does not bubble, hence onInvalidCapture on the form rather than a handler
  // per field. The first event of the batch is the first invalid field; the rest
  // are ignored so the page does not jump past it to the last one.
  const handleInvalid = (e) => {
    if (invalidHandledRef.current) return;
    invalidHandledRef.current = true;
    // Cleared once the whole batch of invalid events for this attempt has
    // fired, so the next Submit press starts over.
    setTimeout(() => { invalidHandledRef.current = false; }, 0);

    const field = e.target;
    // Focus first, without scrolling, then scroll deliberately. focus() does its
    // own scrolling, which would fight the scrollIntoView below.
    if (typeof field.focus === 'function') {
      field.focus({ preventScroll: true });
    }
    // Centred, not aligned to the top: a field in the middle of the viewport is
    // clear of anything pinned to the top of the page - a sticky header, a
    // banner - without hardcoding a header height here that would silently drift
    // out of date when that header changes.
    if (typeof field.scrollIntoView === 'function') {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // The in-page error banner, which renders at the top of the form while the
  // visitor pressing Submit is at the bottom of it.
  //
  // What is left for the banner to say is now only the failed request: the
  // server rejected the submission, or it never arrived. That is the one message
  // with no control to send anyone to, so it is the one that has to come to
  // them. Every other reason a submit is refused is attached to a field and is
  // handled by taking the visitor to that field instead.
  const errorBannerRef = useRef(null);

  useEffect(() => {
    if (!error || !errorBannerRef.current) return;
    errorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // A collapsed additional property is unmounted, not hidden, so its required
    // address fields are not in the DOM and the browser has nothing to refuse
    // the submit on. Without this a visitor can add a property, collapse it and
    // send a property with no address at all.
    //
    // Checked here rather than by keeping the fields mounted and hidden, which
    // was the other obvious fix. A required control that is display:none is
    // still a validation candidate but cannot be focused, so Chrome refuses the
    // submit and shows the visitor nothing whatsoever - which is the silent dead
    // Submit button this form was fixed for last time, reintroduced by the fix.
    // Validating the data instead leaves the collapse behaviour completely
    // untouched: nothing about what renders, or when, changes.
    const incompleteProperty = additionalProperties.find(
      p => !expandedProperties[p.id] && firstMissingPropertyField(p)
    );
    if (incompleteProperty) {
      // No banner. Expanding the property and handing the empty field to the
      // browser puts the visitor at the control with the native message on it,
      // which says more than a sentence at the top of the page about a property
      // they cannot see.
      pendingPropertyFieldRef.current = {
        propertyId: incompleteProperty.id,
        fieldName: firstMissingPropertyField(incompleteProperty),
      };
      setExpandedProperties(prev => ({ ...prev, [incompleteProperty.id]: true }));
      setSubmitting(false);
      return;
    }

    // Both checks below are now unreachable in any browser with constraint
    // validation: each control carries required, so the browser blocks the
    // submit and never calls this function. They stay as the fallback for a
    // browser that does not, and the conditions are kept in step with what is
    // actually rendered - the terms check fires only when the disclaimer block
    // with the checkbox in it is on the page, because requiring a box that was
    // never rendered would make the form unsubmittable.
    if (!formData.snowfallThreshold) {
      setError('Please select when you\'d like service');
      setSubmitting(false);
      return;
    }

    if (disclaimerEnabled && disclaimerText && !formData.termsAccepted) {
      setError('You must agree to the Payment Terms & Conditions');
      setSubmitting(false);
      return;
    }

    // Text-message choice. No choice is an inline message under the radios,
    // which is where the visitor has to act; Yes with an untextable phone
    // keeps the banner it always had. Same rules as the server.
    const sms = smsIntakeDecision(smsAvailable, formData.smsChoice, formData.phone);
    if (sms.error) {
      if (sms.field === 'smsConsent') setSmsChoiceError(sms.error);
      else setError(sms.error);
      setSubmitting(false);
      return;
    }
    setSmsChoiceError('');

    try {
      // Prepare additional properties (remove temporary IDs)
      const cleanedProperties = additionalProperties.map(({ id, ...rest }) => rest);
      const { smsChoice, ...fields } = formData;

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/public/intake/${companyCode}`,
        {
          ...fields,
          smsConsent: sms.smsConsent,
          // Only when the radios were rendered; the server asks for a choice
          // only from a form that showed one.
          ...(sms.smsChoiceShown ? { smsChoiceShown: true } : {}),
          additionalProperties: cleanedProperties,
          ...(addonOptions.length > 0 ? { addons: intakeAddonsPayload(addonOptions, addonChoices) } : {}),
          termsSha256: disclaimerSha256,
        }
      );
      setSubmitted(true);
    } catch (err) {
      // The server's own 400 for no choice lands under the radios when they
      // are on the page; otherwise (a form loaded before the company turned
      // texting on) the banner says it and a reload shows the radios.
      if (err.response?.data?.field === 'smsConsent' && smsAvailable) {
        setSmsChoiceError(err.response.data.error);
        return;
      }
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="intake-page">
        <div className="intake-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="intake-page">
        <div className="intake-container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h1>Thank You!</h1>
            <p>Your information has been submitted successfully.</p>
            <p>We will contact you shortly to discuss your snow removal needs.</p>
            {companyName && <p className="company-name">{companyName}</p>}
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error && !companyName) {
    return (
      <div className="intake-page">
        <div className="intake-container">
          <div className="error-message">
            <h1>Oops!</h1>
            <p>{error}</p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="intake-page">
      <div className="intake-container">
        <div className="intake-header">
          <h1>{companyName || getAppName()}</h1>
          <p className="intake-subtitle">New Customer Sign Up</p>
          <a className="intake-rates-link" href={`/rates/${encodeURIComponent(companyCode)}`}>
            See our rates
          </a>
        </div>

        <form onSubmit={handleSubmit} onInvalidCapture={handleInvalid} className="intake-form">
          {error && <div ref={errorBannerRef} className="alert alert-error">{error}</div>}

          <div className="form-section">
            <h2>Contact Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  className="form-input"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  autoComplete="given-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  className="form-input"
                  value={formData.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  value={formData.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                  placeholder="555.555.5555"
                />
                <small style={{ color: '#888', fontSize: '0.8em' }}>Enter 10-digit phone number (area code + number)</small>
                {smsAvailable && (
                  <SmsConsentChoice
                    yesText={smsConsentText}
                    noText={smsDeclineText}
                    value={formData.smsChoice}
                    onChange={(choice) => {
                      setFormData(prev => ({ ...prev, smsChoice: choice }));
                      setSmsChoiceError('');
                    }}
                    error={smsChoiceError}
                  />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                {/* Required because Square cannot invoice a customer with no
                    email. pattern is the Settings business email rule,
                    something@something with no whitespace, and the server
                    applies the same rule to the submission. */}
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  pattern="[^\s@]+@[^\s@]+"
                  autoComplete="email"
                  placeholder="your@email.com"
                />
              </div>
            </div>
          </div>

          <div className="form-section property-section">
            <h2>
              <span className="property-number">Property 1</span>
              {additionalProperties.length > 0 && <span className="primary-badge">Primary</span>}
            </h2>

            <div className="form-group">
              <label className="form-label">Street Address *</label>
              <input
                type="text"
                name="address"
                className="form-input"
                value={formData.address}
                onChange={handleChange}
                required
                // address-line1, not street-address: street-address is the
                // multi-line token meant for a textarea, and a browser
                // honouring that is free to look for a textarea to fill.
                autoComplete="address-line1"
                placeholder="123 Main St"
              />
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label className="form-label">City *</label>
                <input
                  type="text"
                  name="city"
                  className="form-input"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  autoComplete="address-level2"
                />
              </div>
              <div className="form-group">
                <label className="form-label">State *</label>
                <input
                  type="text"
                  name="state"
                  className="form-input"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  maxLength={2}
                  autoComplete="address-level1"
                  placeholder="OH"
                />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP *</label>
                <input
                  type="text"
                  name="zip"
                  className="form-input"
                  value={formData.zip}
                  onChange={handleChange}
                  required
                  autoComplete="postal-code"
                  placeholder="44xxx"
                />
              </div>
            </div>

            <div className="checkbox-grid property-services">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="driveway"
                  checked={formData.driveway}
                  onChange={handleChange}
                />
                <span>Driveway</span>
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="sidewalks"
                  checked={formData.sidewalks}
                  onChange={handleChange}
                />
                <span>Sidewalks</span>
              </label>
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  name="walkways"
                  checked={formData.walkways}
                  onChange={handleChange}
                />
                <span>Walkways</span>
              </label>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="intake-salt">Salt</label>
              <select
                id="intake-salt"
                name="saltPreference"
                className="form-select"
                value={formData.saltPreference}
                onChange={handleChange}
              >
                {SALT_CHOICES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* Driveway size, primary address.
                The whole block, framing sentence included, is absent when the
                company has no active tiers. An empty dropdown would be worse
                than no question, and the form must still submit. */}
            {sizeOptions.length > 0 && (
              <div className="form-group size-question">
                <label className="form-label">Driveway size</label>
                <p className="size-framing">{SIZE_FRAMING}</p>
                <p className="size-helper">{SIZE_HELPER}</p>
                <select
                  name="requestedSnowTierId"
                  className="form-select"
                  value={formData.requestedSnowTierId}
                  onChange={handleChange}
                  /* Required, but gated on there being something to choose.
                     The gate is written out rather than left to the enclosing
                     sizeOptions.length check, so unwrapping that conditional
                     later cannot turn this into a required field with no
                     options - which would make the form unsubmittable for a
                     company that deactivated every tier. */
                  required={sizeOptions.length > 0}
                >
                  <option value="">Select a size</option>
                  {sizeOptions.map(option => (
                    <option key={option.id} value={String(option.id)}>{sizeOptionLabel(option)}</option>
                  ))}
                  {/* Last, after the sizes, so it reads as the escape hatch it
                      is rather than as the easiest thing to pick. It satisfies
                      required, so the question can be mandatory without forcing
                      a guess: a wrong size confidently stated is worse for both
                      sides than an honest "not sure". */}
                  <option value={NOT_SURE_SIZE}>Not sure</option>
                </select>
                {sizeDefinition(formData.requestedSnowTierId) && (
                  <p className="size-definition">{sizeDefinition(formData.requestedSnowTierId)}</p>
                )}
                <p className="size-confirmation">{sizeConfirmationText}</p>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notes for this property</label>
              <textarea
                name="notes"
                className="form-textarea"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                // Free text the customer writes; never an autofill target. A
                // production signup arrived with its street address here.
                autoComplete="off"
                placeholder="Gate codes, special instructions..."
              />
            </div>
          </div>

          {/* Additional Properties.
              Each section carries an id, which is how a blocked submit finds
              this property's fields again after expanding it. Scoping that
              lookup to the section matters: every property renders a control
              called address, and the primary address is not even in this list. */}
          {additionalProperties.map((property, index) => (
            <div key={property.id} id={`property-${property.id}`} className="form-section property-section additional-property">
              <div className="property-header">
                <h2 onClick={() => togglePropertyExpanded(property.id)} className="property-toggle">
                  <span className="toggle-icon">{expandedProperties[property.id] ? '▼' : '▶'}</span>
                  <span className="property-number">Property {index + 2}</span>
                  {property.address && <span className="property-preview">- {property.address}</span>}
                </h2>
                <button
                  type="button"
                  className="btn-remove-property"
                  onClick={() => removeProperty(property.id)}
                  title="Remove this property"
                >
                  Remove
                </button>
              </div>

              {expandedProperties[property.id] && (
                <div className="property-fields">
                  <div className="form-group">
                    <label className="form-label">Street Address *</label>
                    <input
                      type="text"
                      name="address"
                      className="form-input"
                      value={property.address}
                      onChange={(e) => handlePropertyChange(property.id, e)}
                      required
                      placeholder="456 Oak Ave"
                    />
                  </div>

                  <div className="form-row form-row-3">
                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        name="city"
                        className="form-input"
                        value={property.city}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">State *</label>
                      <input
                        type="text"
                        name="state"
                        className="form-input"
                        value={property.state}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                        required
                        maxLength={2}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ZIP *</label>
                      <input
                        type="text"
                        name="zip"
                        className="form-input"
                        value={property.zip}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                        required
                      />
                    </div>
                  </div>

                  <div className="checkbox-grid property-services">
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        name="driveway"
                        checked={property.driveway}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                      />
                      <span>Driveway</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        name="sidewalks"
                        checked={property.sidewalks}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                      />
                      <span>Sidewalks</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        name="walkways"
                        checked={property.walkways}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                      />
                      <span>Walkways</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor={`intake-salt-${property.id}`}>Salt</label>
                    <select
                      id={`intake-salt-${property.id}`}
                      name="saltPreference"
                      className="form-select"
                      value={property.saltPreference}
                      onChange={(e) => handlePropertyChange(property.id, e)}
                    >
                      {SALT_CHOICES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>

                  {/* Driveway size, additional property.
                      The select and the definition line only. The framing
                      sentence and the helper question are rendered once, on the
                      primary address, so a form with three properties does not
                      repeat the same long sentence three times. The value is
                      still per property and still starts unselected. */}
                  {sizeOptions.length > 0 && (
                    <div className="form-group size-question">
                      <label className="form-label">Driveway size for this property</label>
                      <select
                        name="requestedSnowTierId"
                        className="form-select"
                        value={property.requestedSnowTierId || ''}
                        onChange={(e) => handlePropertyChange(property.id, e)}
                        required={sizeOptions.length > 0}
                      >
                        <option value="">Select a size</option>
                        {sizeOptions.map(option => (
                          <option key={option.id} value={String(option.id)}>{sizeOptionLabel(option)}</option>
                        ))}
                        <option value={NOT_SURE_SIZE}>Not sure</option>
                      </select>
                      {sizeDefinition(property.requestedSnowTierId) && (
                        <p className="size-definition">{sizeDefinition(property.requestedSnowTierId)}</p>
                      )}
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Notes for this property</label>
                    <textarea
                      name="notes"
                      className="form-textarea"
                      value={property.notes}
                      onChange={(e) => handlePropertyChange(property.id, e)}
                      rows={2}
                      autoComplete="off"
                      placeholder="Gate codes, special instructions..."
                    />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add Another Property Button */}
          <div className="add-property-section">
            <button
              type="button"
              className="btn-add-property"
              onClick={addProperty}
            >
              + Add Another Property
            </button>
            <p className="add-property-hint">Have multiple addresses? Add them all here.</p>
          </div>

          {leadSources.length > 0 && (
            <div className="form-section">
              <h2>How did you find us?</h2>
              <div className="form-group">
                <select
                  name="leadSource"
                  className="form-select"
                  value={formData.leadSource}
                  onChange={handleChange}
                >
                  <option value="">-- Select --</option>
                  {leadSources.map(source => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
              </div>
              {formData.leadSource === 'referral' && (
                <div className="form-group">
                  <label className="form-label">Who referred you?</label>
                  <input
                    type="text"
                    name="referredByName"
                    className="form-input"
                    value={formData.referredByName}
                    onChange={handleChange}
                    placeholder="Referrer's name"
                  />
                </div>
              )}
              {formData.leadSource === 'other' && (
                <div className="form-group">
                  <label className="form-label">Please specify</label>
                  <input
                    type="text"
                    name="leadSourceOther"
                    className="form-input"
                    value={formData.leadSourceOther}
                    onChange={handleChange}
                    placeholder="How did you hear about us?"
                  />
                </div>
              )}
            </div>
          )}

          <div className="form-section">
            <h2>When would you like service? *</h2>
            <div className="form-group">
              <select
                name="snowfallThreshold"
                className="form-select"
                value={formData.snowfallThreshold}
                onChange={handleChange}
                required
              >
                <option value="">-- Select --</option>
                <option value="any">Any amount of snow</option>
                <option value="1">1 inch or more</option>
                <option value="2">2 inches or more</option>
                <option value="3">3 inches or more</option>
                <option value="4">4 inches or more</option>
              </select>
              <p className="field-hint">Select the minimum snowfall depth before you want service.</p>
            </div>
          </div>

          <div className="form-section">
            <h2>Service Preference *</h2>
            <div className="form-group">
              <div className="radio-options">
                <label className="radio-item">
                  <input
                    type="radio"
                    name="servicePreference"
                    value="seasonal"
                    checked={formData.servicePreference === 'seasonal'}
                    onChange={handleChange}
                  />
                  <span className="radio-label">
                    <strong>Seasonal Contract</strong>
                    <span className="radio-description">Contracted for regular service throughout the season</span>
                  </span>
                </label>
                <label className="radio-item">
                  <input
                    type="radio"
                    name="servicePreference"
                    value="on_call"
                    checked={formData.servicePreference === 'on_call'}
                    onChange={handleChange}
                  />
                  <span className="radio-label">
                    <strong>On-Call / As Needed</strong>
                    <span className="radio-description">Pay per service when snow removal is performed</span>
                    {onCallPremiumPercent && (
                      <span className="radio-description on-call-premium-note">
                        On-call service is billed at {Number(onCallPremiumPercent.toFixed(2))}% above seasonal rates.
                      </span>
                    )}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {addonOptions.length > 0 && (
            <div className="form-section">
              <h2>Extra services</h2>
              <p className="field-hint">Optional. Tick any you would like and we will confirm them with you.</p>
              <div className="checkbox-grid intake-addons">
                {addonOptions.map(addon => {
                  const choice = addonChoices[addon.id] || {};
                  return (
                    <div key={addon.id} className="intake-addon">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={Boolean(choice.checked)}
                          onChange={(e) => setAddonChoices(prev => ({
                            ...prev, [addon.id]: { ...prev[addon.id], checked: e.target.checked },
                          }))}
                        />
                        <span>{addon.name}</span>
                      </label>
                      {choice.checked && addon.detailLabel && (
                        <input
                          type="text"
                          className="form-input intake-addon-detail"
                          aria-label={addon.detailLabel}
                          placeholder={`${addon.detailLabel} (optional)`}
                          maxLength={60}
                          autoComplete="off"
                          value={choice.detail || ''}
                          onChange={(e) => setAddonChoices(prev => ({
                            ...prev, [addon.id]: { ...prev[addon.id], detail: e.target.value },
                          }))}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Honeypot field - hidden from users */}
          <div style={{ position: 'absolute', left: '-9999px' }}>
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {/* Payment Terms & Conditions Disclaimer */}
          {disclaimerEnabled && disclaimerText && (
            <div className="terms-disclaimer">
              <h3>Payment Terms & Conditions</h3>
              <p className="terms-text">
                By submitting this form, you acknowledge and agree to the following terms:
              </p>
              <p className="terms-text">
                {disclaimerText.replace(/{company_name}/g, companyName)}
              </p>
              <label className="terms-checkbox">
                {/* required, like every other mandatory control on this form.
                    It used to be the one exception, validated only in
                    handleSubmit, which is why it was also the only thing that
                    ever reached the error banner at the top of the page - a
                    message about a checkbox, rendered where the checkbox is
                    not. With required the browser refuses the submit, fires
                    invalid, and handleInvalid centres the box itself with the
                    native message on it. */}
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  required
                />
                <span>I have read and agree to the Payment Terms & Conditions <span className="required">*</span></span>
              </label>
            </div>
          )}

          {/* Email opt-in */}
          <div style={{ marginBottom: '16px' }}>
            <label className="terms-checkbox">
              <input
                type="checkbox"
                name="emailSubscribed"
                checked={formData.emailSubscribed}
                onChange={handleChange}
              />
              <span>Subscribe to email updates</span>
            </label>
            <p style={{ fontSize: '13px', color: 'var(--color-text-light)', margin: '4px 0 0 28px' }}>
              Receive notifications about upcoming storms, service updates, and seasonal announcements
            </p>
          </div>

          <button type="submit" className="btn btn-primary btn-submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>

          <p className="form-footer">
            By submitting this form, you agree to be contacted about snow removal services.
          </p>
        </form>
      </div>
      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .intake-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .intake-container {
    background: white;
    border-radius: 16px;
    max-width: 600px;
    width: 100%;
    padding: 32px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  .intake-header {
    text-align: center;
    margin-bottom: 32px;
  }

  .intake-header h1 {
    font-size: 1.75rem;
    color: #1e3a5f;
    margin: 0 0 8px 0;
  }

  .intake-subtitle {
    color: #6b7280;
    font-size: 1rem;
    margin: 0;
  }

  .intake-rates-link {
    display: inline-block;
    margin-top: 8px;
    color: #2563eb;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .form-section {
    margin-bottom: 24px;
  }

  .form-section h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .form-row-3 {
    grid-template-columns: 2fr 1fr 1fr;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }

  .form-input,
  .form-select,
  .form-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }

  .form-input:focus,
  .form-select:focus,
  .form-textarea:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .form-textarea {
    resize: vertical;
    min-height: 80px;
  }

  .size-framing,
  .size-helper,
  .size-definition,
  .size-confirmation {
    font-size: 0.875rem;
    line-height: 1.45;
    color: #4b5563;
  }

  .size-framing {
    margin: 0 0 6px 0;
  }

  .size-helper {
    margin: 0 0 10px 0;
    font-weight: 500;
    color: #374151;
  }

  .size-definition {
    margin: 8px 0 0 0;
    color: #6b7280;
  }

  .size-confirmation {
    margin: 10px 0 0 0;
    padding: 10px 12px;
    background: #f3f4f6;
    border-radius: 6px;
    color: #4b5563;
  }

  .checkbox-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 12px;
    background: #f9fafb;
    border-radius: 8px;
    transition: background 0.2s;
  }

  .checkbox-item:hover {
    background: #f3f4f6;
  }

  .checkbox-item input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #2563eb;
  }

  .checkbox-item span {
    font-size: 0.95rem;
    color: #374151;
  }

  /* One add-on per row, so a detail box sits under its own checkbox. */
  .checkbox-grid.intake-addons {
    grid-template-columns: 1fr;
  }

  .intake-addon-detail {
    margin-top: 8px;
  }

  .btn-submit {
    width: 100%;
    padding: 14px;
    font-size: 1.1rem;
    font-weight: 600;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
  }

  .btn-submit:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .btn-submit:active:not(:disabled) {
    transform: scale(0.98);
  }

  .btn-submit:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  .form-footer {
    text-align: center;
    font-size: 0.8rem;
    color: #9ca3af;
    margin-top: 16px;
  }

  .alert {
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 0.9rem;
  }

  .alert-error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }

  .success-message {
    text-align: center;
    padding: 40px 20px;
  }

  .success-icon {
    width: 64px;
    height: 64px;
    background: #10b981;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    margin: 0 auto 24px;
  }

  .success-message h1 {
    color: #1e3a5f;
    margin: 0 0 16px 0;
  }

  .success-message p {
    color: #6b7280;
    margin: 0 0 8px 0;
  }

  .success-message .company-name {
    font-style: italic;
    margin-top: 24px;
    color: #1e3a5f;
  }

  .error-message {
    text-align: center;
    padding: 40px 20px;
  }

  .error-message h1 {
    color: #dc2626;
    margin: 0 0 16px 0;
  }

  .error-message p {
    color: #6b7280;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Multiple Properties Styles */
  .property-section {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
    background: #fafafa;
  }

  .property-section h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 16px;
  }

  .property-number {
    color: #1e3a5f;
  }

  .primary-badge {
    font-size: 0.7rem;
    background: #2563eb;
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 500;
  }

  .property-services {
    margin-top: 12px;
  }

  .additional-property {
    border-color: #7c3aed;
    background: #faf5ff;
  }

  .property-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .property-toggle {
    cursor: pointer;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    border: none;
    padding: 0;
  }

  .toggle-icon {
    color: #7c3aed;
    font-size: 0.8rem;
  }

  .property-preview {
    font-weight: 400;
    color: #6b7280;
    font-size: 0.9rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  .btn-remove-property {
    background: none;
    border: 1px solid #dc2626;
    color: #dc2626;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-remove-property:hover {
    background: #dc2626;
    color: white;
  }

  .property-fields {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9d5ff;
  }

  .add-property-section {
    text-align: center;
    padding: 20px;
    margin-bottom: 24px;
  }

  .btn-add-property {
    background: white;
    border: 2px dashed #7c3aed;
    color: #7c3aed;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-add-property:hover {
    background: #7c3aed;
    color: white;
    border-style: solid;
  }

  .add-property-hint {
    margin: 8px 0 0 0;
    font-size: 0.8rem;
    color: #9ca3af;
  }

  .field-hint {
    margin: 6px 0 0 0;
    font-size: 0.8rem;
    color: #6b7280;
  }

  /* Terms Disclaimer Styles */
  .terms-disclaimer {
    background: #f9fafb;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .terms-disclaimer h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 12px 0;
  }

  .terms-text {
    font-size: 0.9em;
    color: #4b5563;
    line-height: 1.6;
    margin: 0 0 12px 0;
  }

  .terms-text:last-of-type {
    margin-bottom: 16px;
  }

  .terms-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
    padding: 12px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    transition: border-color 0.2s;
  }

  .terms-checkbox:hover {
    border-color: #2563eb;
  }

  .terms-checkbox input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    accent-color: #2563eb;
    flex-shrink: 0;
  }

  .terms-checkbox span {
    font-size: 0.95rem;
    color: #374151;
    font-weight: 500;
  }

  .terms-checkbox .required {
    color: #dc2626;
    font-weight: 600;
  }

  .terms-checkbox input[type="radio"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    accent-color: #2563eb;
    flex-shrink: 0;
  }

  .sms-consent-choice {
    border: none;
    padding: 0;
    margin: 8px 0 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .sms-consent-legend {
    font-size: 0.9rem;
    font-weight: 600;
    color: #374151;
    padding: 0;
    margin-bottom: 4px;
  }

  .sms-consent-choice[aria-invalid="true"] .terms-checkbox {
    border-color: #dc2626;
  }

  .sms-consent-error {
    color: #dc2626;
    font-size: 0.85rem;
    font-weight: 500;
  }

  /* Radio Options Styles */
  .radio-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .radio-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    padding: 16px;
    background: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    transition: all 0.2s;
  }

  .radio-item:hover {
    border-color: #2563eb;
    background: #f0f7ff;
  }

  .radio-item:has(input:checked) {
    border-color: #2563eb;
    background: #eff6ff;
  }

  .radio-item input[type="radio"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    accent-color: #2563eb;
    flex-shrink: 0;
  }

  .radio-label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .radio-label strong {
    font-size: 1rem;
    color: #1e3a5f;
  }

  .radio-description {
    font-size: 0.85rem;
    color: #6b7280;
  }

  .on-call-premium-note {
    display: block;
    margin-top: 4px;
    color: #92400e;
    font-weight: 500;
  }

  @media (max-width: 640px) {
    .intake-container {
      padding: 24px 20px;
    }

    .form-row,
    .form-row-3 {
      grid-template-columns: 1fr;
    }

    .checkbox-grid {
      grid-template-columns: 1fr;
    }

    .intake-header h1 {
      font-size: 1.5rem;
    }
  }
`;

export default IntakeForm;
